import axios from 'axios';

/**
 * Strava API Service
 * Handles communication with Strava API using Auth0 access token
 */

class StravaApiService {
  constructor() {
    this.baseUrl = 'https://www.strava.com/api/v3';
  }

  /**
   * Get activities from Strava API
   * @param {string} stravaAccessToken - Strava access token (not Auth0 token)
   * @param {number} after - Unix timestamp to get activities after
   * @param {number} before - Unix timestamp to get activities before
   * @param {number} perPage - Number of activities per page (max 200)
   * @returns {Promise<Array>} Array of activities
   */
  async getActivities(stravaAccessToken, after = null, before = null, perPage = 200, page = 1) {
    try {
      console.log('Making Strava API call to /athlete/activities');
      console.log('Token (first 20 chars):', stravaAccessToken.substring(0, 20) + '...');
      
      const params = new URLSearchParams({
        per_page: perPage.toString(),
        page: page.toString()
      });
      
      if (after) params.append('after', after.toString());
      if (before) params.append('before', before.toString());

      const url = `${this.baseUrl}/athlete/activities?${params}`;
      console.log('Request URL:', url);

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${stravaAccessToken}`,
          'User-Agent': 'WhacksOffPWA'
        }
      });

      console.log('Strava API response:', response.status, response.data?.length || 'no data');
      return response.data;
    } catch (error) {
      console.error('Error fetching activities:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Strava access token is expired or invalid. Please re-authenticate with Strava.');
      }
      
      throw new Error(`Failed to fetch activities: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get all activities with pagination
   * @param {string} accessToken - Strava access token
   * @param {number} after - Unix timestamp to get activities after
   * @returns {Promise<Array>} Array of all activities
   */
  async getAllActivities(accessToken, after = null) {
    try {
      let allActivities = [];
      let page = 1;
      const perPage = 200;
      let hasMore = true;

      while (hasMore) {
        console.log(`Fetching page ${page} of activities...`);
        
        const activities = await this.getActivities(accessToken, after, null, perPage, page);
        
        if (activities.length === 0) {
          hasMore = false;
        } else {
          allActivities = allActivities.concat(activities);
          
          // If we got less than perPage, we've reached the end
          if (activities.length < perPage) {
            hasMore = false;
          } else {
            page++;
          }
        }
      }

      console.log(`Fetched ${allActivities.length} total activities across ${page} pages`);
      return allActivities;
    } catch (error) {
      console.error('Error fetching all activities:', error);
      throw error;
    }
  }

  /**
   * Get activities for a specific gear
   * @param {string} accessToken - Auth0 access token
   * @param {string} gearId - Strava gear ID
   * @param {number} after - Unix timestamp to get activities after
   * @returns {Promise<Array>} Array of activities for the gear
   */
  async getActivitiesForGear(accessToken, gearId, after = null) {
    try {
      // Fetch all activities with pagination to ensure we get enough for the gear
      const allActivities = await this.getAllActivities(accessToken, after);
      const gearActivities = allActivities.filter(activity => activity.gear_id === gearId);
      console.log(`Found ${gearActivities.length} activities for gear ${gearId} out of ${allActivities.length} total activities`);
      return gearActivities;
    } catch (error) {
      console.error('Error fetching activities for gear:', error);
      throw error;
    }
  }

  /**
   * Find the last activity matching a pattern
   * @param {string} accessToken - Auth0 access token
   * @param {string} gearId - Strava gear ID
   * @param {string} pattern - Pattern to match in activity name
   * @param {number} after - Unix timestamp to search after
   * @returns {Promise<Object|null>} Last matching activity or null
   */
  async findLastMatchingActivity(accessToken, gearId, pattern, after = null) {
    try {
      const activities = await this.getActivitiesForGear(accessToken, gearId, after);
      
      console.log(`Searching for pattern "${pattern}" in ${activities.length} activities`);
      console.log('Activity names:', activities.map(a => a.name).slice(0, 10)); // Show first 10 activity names
      
      // Filter activities that match the pattern
      const matchingActivities = activities.filter(activity => 
        activity.name && activity.name.includes(pattern)
      );

      console.log(`Found ${matchingActivities.length} matching activities`);
      if (matchingActivities.length > 0) {
        console.log('Matching activities:', matchingActivities.map(a => a.name));
      }

      // Return the most recent matching activity
      return matchingActivities.length > 0 ? matchingActivities[0] : null;
    } catch (error) {
      console.error('Error finding last matching activity:', error);
      throw error;
    }
  }

  /**
   * Calculate total distance for activities
   * @param {Array} activities - Array of activities
   * @returns {number} Total distance in meters
   */
  calculateTotalDistance(activities) {
    return activities.reduce((total, activity) => total + (activity.distance || 0), 0);
  }

  /**
   * Get wax tracking data
   * @param {string} accessToken - Auth0 access token
   * @param {Object} settings - User settings
   * @returns {Promise<Object>} Wax tracking data
   */
  async getWaxTrackingData(accessToken, settings, forceRefresh = false) {
    try {
      const { matchPattern, waxIntervalDistance, gearId } = settings;
      
      let searchAfter = null;
      
      if (forceRefresh) {
        // Force refresh: ignore cache completely, search from 6 months ago
        searchAfter = Math.floor(Date.now() / 1000) - (6 * 30 * 24 * 60 * 60);
        console.log('Force refresh: searching from 6 months ago (ignoring cache)');
      } else {
        // Normal load: check if we have a cached last wax date for optimization
        const cachedLastWaxDate = localStorage.getItem('last_wax_date');
        
        if (cachedLastWaxDate) {
          // Use the cached date as the search start point, minus 1 second to include the chain activity
          searchAfter = Math.floor(new Date(cachedLastWaxDate).getTime() / 1000) - 1;
          console.log('Using cached last wax date for optimization (minus 1 second):', cachedLastWaxDate);
        } else {
          // Fall back to 6 months ago if no cache
          searchAfter = Math.floor(Date.now() / 1000) - (6 * 30 * 24 * 60 * 60);
          console.log('No cached last wax date, searching from 6 months ago');
        }
      }
      
      // Get all activities for the gear from the search point
      const allActivities = await this.getActivitiesForGear(accessToken, gearId, searchAfter);
      
      // Find the last wax activity from the fetched activities
      const matchingActivities = allActivities.filter(activity => 
        activity.name && activity.name.includes(matchPattern)
      );
      
      console.log(`Found ${matchingActivities.length} activities matching pattern "${matchPattern}"`);
      if (matchingActivities.length > 0) {
        console.log('Matching activities:', matchingActivities.map(a => `${a.name} (${a.start_date})`));
      }
      
      // Sort by date to get the most recent
      const sortedMatchingActivities = matchingActivities.sort((a, b) => 
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
      );
      
      const lastWaxActivity = sortedMatchingActivities.length > 0 ? sortedMatchingActivities[0] : null;

      if (!lastWaxActivity) {
        return {
          hasLastWax: false,
          message: `No wax activities found. Start tracking by adding "${matchPattern}" to your activity names!`,
          distanceSinceWax: 0,
          distanceToWax: waxIntervalDistance,
          lastWaxDate: null
        };
      }

      // Get activities since the last wax (from the already fetched data)
      // Only include activities that happened AFTER the last wax activity
      const lastWaxTimestamp = Math.floor(new Date(lastWaxActivity.start_date).getTime() / 1000) - 1;
      console.log('Last wax timestamp (minus 1 second):', lastWaxTimestamp, '(', new Date(lastWaxTimestamp * 1000).toISOString(), ')');
      
      // Filter the already fetched activities to only include those after the last wax
      console.log('Filtering activities:');
      console.log('- Last wax timestamp:', lastWaxTimestamp);
      console.log('- Last wax date:', new Date(lastWaxTimestamp * 1000).toISOString());
      
      const activitiesSinceWax = allActivities.filter(activity => {
        const activityTime = Math.floor(new Date(activity.start_date).getTime() / 1000);
        const isAfter = activityTime > lastWaxTimestamp;
        console.log(`- Activity: ${activity.name} (${activity.start_date}) - ${activityTime} > ${lastWaxTimestamp} = ${isAfter}`);
        return isAfter;
      });
      
      console.log('Activities since last wax (filtered from fetched data):', activitiesSinceWax.length);

      // Calculate total distance since last wax
      const distanceSinceWaxMeters = this.calculateTotalDistance(activitiesSinceWax);
      const distanceSinceWaxKm = Math.floor(distanceSinceWaxMeters / 1000);

      // Determine if wax is needed
      const needsWax = distanceSinceWaxKm >= waxIntervalDistance;
      const distanceToWax = needsWax ? 0 : waxIntervalDistance - distanceSinceWaxKm;

      // Store the last wax date in localStorage for future optimization
      localStorage.setItem('last_wax_date', lastWaxActivity.start_date);
      console.log('Cached last wax date:', lastWaxActivity.start_date);

      return {
        hasLastWax: true,
        needsWax,
        distanceSinceWax: distanceSinceWaxKm,
        distanceToWax,
        lastWaxDate: lastWaxActivity.start_date,
        lastWaxActivity: lastWaxActivity.name,
        lastWaxActivityId: lastWaxActivity.id,
        activitiesCount: activitiesSinceWax.length
      };

    } catch (error) {
      console.error('Error getting wax tracking data:', error);
      
      // If it's an authentication error, provide helpful message
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        throw new Error('Authentication failed with Strava API. The Auth0 token cannot be used directly with Strava. You may need to implement a different approach to get the Strava access token.');
      }
      
      throw error;
    }
  }
}

const stravaApiService = new StravaApiService();
export default stravaApiService;
